import React, { useRef, useState } from "react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ModelElementType } from "@/components/entities";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { LoadingState } from "@/components/common/LoadingState";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
  OVERVIEW_LIST_ROWS,
} from "@/components/main/pages/pageColumn";
import {
  useDuplicateSessionNotice,
  useModelsPolling,
} from "@/components/main/hooks/useModelsPolling";
import { HeroSearch } from "@/components/main/overview/HeroSearch";
import { SpaceFilterRule } from "@/components/main/overview/SpaceFilterRule";
import { OrgInviteBanner } from "@/components/main/organizations/OrgInviteBanner";
import { SectionRule } from "components/common/SectionRule";
import { ModelRow } from "@/components/main/overview/ModelRow";
import { ComputedTablesSection } from "@/components/main/overview/ComputedTablesSection";
import { PinnedColumn } from "@/components/main/overview/PinnedColumn";
import { CreateModelStrip } from "@/components/main/overview/CreateModelStrip";
import {
  formatCompactRelativeTime,
  selectAll,
  selectPinned,
  selectRecentForDisplay,
  hasLiveModels,
} from "@/components/main/overview/selectors";
import { useCommandK } from "@/components/main/overview/useCommandK";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

export const OverviewPage: React.FC = () => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showAll, setShowAll] = useState(false);
  const { isOnline, isAppInitialized, modelsInitialized } = useModelsPolling();
  useDuplicateSessionNotice();
  useCommandK(searchInputRef);
  // Same create-model action + settle-in animation used by the team model list.
  const { freshModelId, createModel } = useCreateModel();

  const availableModels = useMainStateStore((state) => state.availableModels);
  const selectedSpaceId = useOrgContextStore((s) => s.selectedSpaceId);
  const setSelectedSpace = useOrgContextStore((s) => s.setSelectedSpace);
  // The rail's space filter narrows every bucket on the page.
  const scoped = selectedSpaceId
    ? availableModels.filter((m) => m.space_id === selectedSpaceId)
    : availableModels;

  const pinned = selectPinned(scoped);
  // Falls back to all models when nothing is recent or pinned yet, so a first
  // login with only shared models still surfaces them here (not an empty hint).
  const recent = selectRecentForDisplay(scoped);
  const all = selectAll(scoped);

  const visibleRows = (showAll ? all.length : recent.length) || 1;
  const listHeight = `${Math.min(visibleRows, OVERVIEW_LIST_ROWS) * 2}rem`;
  // Below the cap the list height matches its content exactly. Keeping overflow
  // hidden below the cap stops a scrollbar flashing while the height transition
  // catches up to a new row.
  const listScrolls = visibleRows > OVERVIEW_LIST_ROWS;

  const onDuplicateSession = (message: string): void =>
    showNotificationGlobal(message);

  const renderRows = (
    models: ModelElementType[],
    showActiveCount: boolean,
  ): React.ReactNode =>
    models.map((model) => (
      <ModelRow
        key={model.model_id}
        model={model}
        showActiveCount={showActiveCount}
        isFresh={String(model.model_id) === freshModelId}
        meta={
          model.last_opened_by_me != null
            ? formatCompactRelativeTime(model.last_opened_by_me)
            : undefined
        }
        onDuplicateSession={onDuplicateSession}
      />
    ));

  if (isAppInitialized && !modelsInitialized) {
    if (!isOnline) {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <div className="offline-empty-state" role="status" aria-live="polite">
            <div className="empty-dot" />
            <div className="empty-text">You&apos;re offline</div>
            <div className="empty-sub">
              Your models will appear when the connection restores
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingState size="large" isLoading label="Loading models..." />
      </div>
    );
  }

  if (!isAppInitialized) {
    return null;
  }

  // Zero models renders the same shell as the populated overview — the Pinned
  // and Recent sections carry their own "…will appear here" hints and the
  // create strip pulses its New model button, so the layout never jumps once the
  // first model lands.
  return (
    <div
      className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6"
      style={{
        fontSize: "var(--font-size)",
        opacity: isOnline ? 1 : 0.55,
        transition: "opacity var(--duration-short) var(--ease-move)",
      }}
    >
      <ColumnTopSpacer />
      <div className="w-full" style={{ maxWidth: "45rem" }}>
        <HeroSearch inputRef={searchInputRef} />
        <OrgInviteBanner />
        {/* 32 above the head, 16 below: the space's name belongs to the lists
            it narrows, so it sits nearer them than to the search. */}
        {selectedSpaceId && (
          <div className="mt-8">
            <SpaceFilterRule
              spaceId={selectedSpaceId}
              onSelect={setSelectedSpace}
            />
          </div>
        )}
        <div
          className={`overview-cols ${selectedSpaceId ? "mt-4" : "mt-12"}`}
          style={{
            gridTemplateColumns: showAll ? "0fr 1fr" : "1fr 1fr",
            columnGap: showAll ? "0px" : "var(--spacing-3xl)",
          }}
        >
          <div
            className="overview-pinned"
            style={{ opacity: showAll ? 0 : 1 }}
            aria-hidden={showAll}
          >
            <PinnedColumn
              models={pinned}
              onDuplicateSession={onDuplicateSession}
            />
          </div>
          <div className="min-w-0">
            <SectionRule
              inset
              icon={
                showAll ? (
                  <DashboardOutlinedIcon sx={{ fontSize: 14 }} />
                ) : (
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                )
              }
              label={showAll ? `All models · ${all.length}` : "Recent"}
              action={{
                label: showAll ? "Show recent" : "Show all",
                onClick: () => setShowAll((v) => !v),
                testId: "toggle-all-models",
              }}
            />
            <div
              className={`overview-list ${
                listScrolls ? "overflow-y-auto" : "overflow-y-hidden"
              } ${showAll && listScrolls ? "pr-1" : ""}`}
              style={{ height: listHeight }}
              data-testid={showAll ? "all-models-list" : undefined}
            >
              {showAll ? (
                renderRows(all, true)
              ) : recent.length > 0 ? (
                renderRows(recent, false)
              ) : (
                <div className="flex items-center gap-2 item-height px-1 text-small text-fg-muted italic">
                  <span className="w-5 shrink-0" />
                  Open a model and it&apos;ll show up here.
                </div>
              )}
            </div>
          </div>
        </div>
        <CreateModelStrip
          hasModels={hasLiveModels(availableModels)}
          onCreateModel={() => void createModel()}
          onCreateCatalogModel={() => void createModel({ catalog: true })}
        />
        <ComputedTablesSection />
      </div>
      <ColumnBottomSpacer />
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>
    </div>
  );
};
